import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import PageShell from './PageShell';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ExternalLink, Maximize2, Loader2, Layers, Smartphone, Lock, GraduationCap, UserCheck } from 'lucide-react';

// Catalogue centralisé : slug → fichier + métadonnées éditoriales + accès par rôle
// audience: rôles autorisés à OUVRIR l'outil. Visiteur non-connecté → traité comme 'eleve' (parcours de découverte).
// Conventions :
//   - 'eleve'        : Élève en préparation permis B / AAC / CS / B96
//   - 'enseignant'   : Moniteur ECSR titulaire en exercice
//   - 'admin'        : Administrateur (toujours accès complet, géré séparément)
export const OUTILS_CATALOG = {
  'analyse-video-giratoire': {
    native_path: '/cours/analyse-giratoire',
    title: 'Analyse vidéo · Giratoire hors agglo',
    subtitle: 'Natif codevoiture.fr · 2 vidéos décodées + grille de révision',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant · Accès libre',
    category: 'Analyse vidéo commentée',
    badge: 'NOUVEAU · 2 vidéos',
  },
  'cours-stop-cedez': {
    native_path: '/cours/stop-cedez',
    title: 'Cours interactif · Stop & Cédez-le-passage',
    subtitle: 'Natif codevoiture.fr · Schéma animé + QCM + grille de révision',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant · Accès libre',
    category: 'Cours interactif',
    badge: 'NOUVEAU · QCM',
  },
  'cours-intersections-priorite': {
    native_path: '/cours/intersections-priorite',
    title: 'Cours interactif · Intersections & priorité à droite',
    subtitle: 'Natif codevoiture.fr · Carrefour animé + QCM + grille de révision',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant · Accès libre',
    category: 'Cours interactif',
    badge: 'NOUVEAU · QCM',
  },
  'cours-en-salle': {
    native_path: '/enseignant/cours-en-salle',
    title: 'Cours en salle · 57 thèmes',
    subtitle: 'Natif codevoiture.fr · Support animation pédagogique',
    audience: ['enseignant'],
    audienceLabel: 'Enseignant ECSR · Animation collective',
    category: 'Support de cours',
    badge: 'NATIF · 57 thèmes',
  },
  'cours-voiture-mobile': {
    native_path: '/cours/eleve/voiture',
    title: 'Cours en voiture — Élève',
    subtitle: 'Natif codevoiture.fr · 38 leçons REMC',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Tous formats',
    category: 'Outil élève natif',
    badge: 'NATIF',
  },
  'rdv-1-2-aac-mobile': {
    native_path: '/formation/parcours',
    title: 'RDV 1 & 2 AAC — Conduite accompagnée',
    subtitle: 'Natif codevoiture.fr · Parents accompagnateurs',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève AAC · Accompagnateur',
    category: 'Conduite accompagnée (AAC)',
    badge: 'AAC · RDV',
  },
  'infractions-sanctions-2026': {
    native_path: '/cours/eleve/infractions-sanctions',
    title: 'Infractions & Sanctions 2026',
    subtitle: 'Natif codevoiture.fr · Barème complet 2026',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Réglementation 2026',
    badge: 'NATIF · 2026',
  },
  'sanctions-2026-fiche-rapide': {
    native_path: '/cours/eleve/infractions-sanctions',
    title: 'Sanctions 2026 — Fiche rapide',
    subtitle: 'Natif codevoiture.fr · Mémo barème',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Tous publics',
    category: 'Réglementation 2026',
    badge: 'NATIF',
  },
  'examen-blanc-grille-officielle': {
    native_path: '/enseignant/examen-blanc-grille',
    title: 'Examen blanc — Grille type permis B',
    subtitle: 'Natif codevoiture.fr · 31 critères · verdict temps réel',
    audience: ['enseignant'],
    audienceLabel: 'Enseignant ECSR · Évaluation finale',
    category: 'Examens blancs',
    badge: 'NATIF',
  },
  'examen-blanc-simulation': {
    native_path: '/cours/eleve/examen-blanc',
    title: 'Examen blanc — Auto-évaluation élève',
    subtitle: 'Natif codevoiture.fr · 5 sections · verdict immédiat',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Préparation examen',
    category: 'Examens blancs',
    badge: 'NATIF',
  },
  'bilan-c1': {
    native_path: '/cours/eleve/bilans',
    title: 'Bilan fin de C1',
    subtitle: 'Natif codevoiture.fr · Maîtriser le maniement',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Bilans REMC',
    badge: 'C1',
  },
  'bilan-c2': {
    native_path: '/cours/eleve/bilans',
    title: 'Bilan fin de C2',
    subtitle: 'Natif codevoiture.fr · Appréhender la route',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Bilans REMC',
    badge: 'C2',
  },
  'bilan-c3': {
    native_path: '/cours/eleve/bilans',
    title: 'Bilan fin de C3',
    subtitle: 'Natif codevoiture.fr · Conditions difficiles',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Bilans REMC',
    badge: 'C3',
  },
  'bilan-c4': {
    native_path: '/cours/eleve/bilans',
    title: 'Bilan fin de C4',
    subtitle: 'Natif codevoiture.fr · Conduite autonome',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Bilans REMC',
    badge: 'C4',
  },
  'bilan-intersections': {
    native_path: '/cours/eleve/bilans',
    title: 'Bilan Intersections',
    subtitle: 'Natif codevoiture.fr · Focus carrefours',
    audience: ['eleve', 'enseignant'],
    audienceLabel: 'Élève · Enseignant ECSR',
    category: 'Bilans REMC',
    badge: 'Focus',
  },
};

// Helper : un rôle (ou null pour visiteur = traité comme 'eleve') a-t-il accès à l'outil ?
export function canAccessTool(tool, userRole, isAdmin) {
  if (!tool) return false;
  if (isAdmin) return true;
  const role = userRole || 'eleve';
  return tool.audience.includes(role);
}

export default function OutilViewerPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const tool = OUTILS_CATALOG[slug];

  if (!tool) return <Navigate to="/cours-interactifs" replace />;

  // Si une version native existe, on redirige automatiquement vers la route React native
  if (tool.native_path) return <Navigate to={tool.native_path} replace />;

  // Contrôle d'accès par rôle
  const allowed = canAccessTool(tool, user?.role, user?.is_admin);
  if (!allowed) {
    return (
      <PageShell>
        <section className="max-w-3xl mx-auto px-4 lg:px-6 mt-16 mb-20" data-testid="outil-access-denied">
          <div className="rounded-2xl border border-red-700/40 bg-gradient-to-br from-red-950/40 via-[#0d0d0e] to-[#0d0d0e] p-8 lg:p-10 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-600/15 border border-red-700/40 flex items-center justify-center text-red-400 mx-auto mb-5">
              <Lock size={28} />
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-2">Accès réservé · profil incompatible</div>
            <h1 className="font-serif-editorial text-white text-[28px] lg:text-[34px] leading-tight">{tool.title}</h1>
            <p className="text-white/65 text-[14px] mt-3 leading-relaxed max-w-xl mx-auto">
              Cet outil est destiné à un profil spécifique : <span className="text-red-400 font-semibold">{tool.audienceLabel}</span>.
              {!user && <> Connecte-toi avec le bon profil pour y accéder.</>}
            </p>

            {/* Profils autorisés */}
            <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-[11.5px] text-white/65 font-mono-spec">
              <span className="text-white/40">Profils autorisés :</span>
              {tool.audience.map((r) => {
                const Icon = r === 'eleve' ? GraduationCap : UserCheck;
                const label = r === 'eleve' ? 'Élève' : r === 'enseignant' ? 'Enseignant ECSR' : 'Futur enseignant';
                return (
                  <span key={r} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-700/30 bg-red-600/10 text-red-300">
                    <Icon size={11} /> {label}
                  </span>
                );
              })}
            </div>

            <div className="mt-7 flex items-center justify-center gap-2 flex-wrap">
              <Link to="/cours-interactifs" data-testid="back-to-tools" className="h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
                <ArrowLeft size={14} /> Tous les outils
              </Link>
              {!user && (
                <Link to="/?auth=login" className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-lg shadow-red-900/40">
                  Me connecter
                </Link>
              )}
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Breadcrumb + titre éditorial codevoiture.fr */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8" data-testid="outil-viewer-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-5" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">{tool.category}</span>
        </nav>

        {/* Bandeau titre */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 lg:p-7 mb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10.5px] font-bold tracking-wider">{tool.badge}</span>
                <span className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec">{tool.audience}</span>
              </div>
              <h1 className="font-serif-editorial text-white text-[28px] lg:text-[40px] leading-[1.02] tracking-tight" data-testid="outil-title">{tool.title}</h1>
              <div className="text-red-400 text-[13px] font-mono-spec mt-1.5">{tool.subtitle}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Link
                to="/cours-interactifs"
                className="h-11 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition"
                data-testid="outil-back-btn"
              >
                <ArrowLeft size={14} /> Tous les outils
              </Link>
              <a
                href={tool.file}
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-lg shadow-red-900/40 transition"
                data-testid="outil-fullscreen-btn"
                title="Ouvrir l'outil en plein écran dans un nouvel onglet"
              >
                <Maximize2 size={14} /> Plein écran
              </a>
            </div>
          </div>
        </div>

        {/* Viewer iframe — charte préservée par codevoiture.fr */}
        <div className="relative rounded-2xl border border-white/10 bg-white overflow-hidden shadow-2xl shadow-red-950/20" style={{ height: 'min(82vh, 1100px)' }} data-testid="outil-iframe-wrapper">
          {!loaded && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[#0d0d0e]">
              <Loader2 size={28} className="animate-spin text-red-400" />
              <div className="text-white/60 text-[13px] font-mono-spec">Chargement de l'outil pédagogique…</div>
            </div>
          )}
          <iframe
            src={tool.file}
            title={tool.title}
            onLoad={() => setLoaded(true)}
            className="w-full h-full border-0 block"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
            data-testid="outil-iframe"
          />
        </div>

        {/* Footer crédits */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3 text-[11.5px] text-white/40 font-mono-spec">
          <div className="inline-flex items-center gap-2">
            <Layers size={12} /> Outil pédagogique intégré · codevoiture.fr
          </div>
          <div className="inline-flex items-center gap-2">
            <Smartphone size={12} /> Si l'affichage est limité sur mobile, ouvre en
            <a href={tool.file} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 ml-1">plein écran <ExternalLink size={10} /></a>.
          </div>
        </div>
      </section>
    </PageShell>
  );
}
